import axios from "axios";
import {
  MessageDirection, UserTypingEvent, TypingUsersList, Conversation,
  ConversationRole, Participant, Presence, UserStatus, User,
} from "@chatscope/use-chat";
import { nanoid } from "@reduxjs/toolkit";
import { BACKEND_URL } from "constants";

function createConversation(id, name) {
  return new Conversation({
    id,
    participants: [ new Participant({ id: name, role: new ConversationRole([]) }) ],
    unreadCounter: 0,
    typingUsers: new TypingUsersList({ items: [] }),
    draft: "",
  });
}

const readMessage = (sender_user_id) => {
  axios.post(`${BACKEND_URL}/read-messages`, { sender_user_id }).catch((e) => console.error(e));
};

export class ChatService {
  eventHandlers = {
    onMessage: () => { }, onConnectionStateChanged: () => { },
    onUserConnected: () => { }, onUserDisconnected: () => { },
    onUserPresenceChanged: () => { }, onUserTyping: () => { },
  };

  constructor(storage, update, socket) {
    this.storage = storage;
    this.socket = socket;
    this.updateState = update;
    this.participants = {};

    this.setupSocketListeners();

    this.waitForUser(() => {
      this.socket.emit("connect_chat", {
        user_id: this.storage.currentUser.id,
        date: Date.now(),
      });
    }, 100);
  }

  setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on("message", async (msg) => {
      console.log("New message received:", msg);
      const message = msg.message;
      
      message.createdTime = message.createdTime ? new Date(message.createdTime) : new Date();
      message.direction = MessageDirection.Incoming;

      let conversationId = msg.conversationId || nanoid();
      let myConversation = this.storage.getState().conversations.find(cv =>
        cv.participants.some(p => p.id === message.senderId)
      );

      if (!myConversation) {
        await this.fetchParticipantsAndAddUser(message.senderId);
        myConversation = createConversation(conversationId, message.senderId);
        this.storage.addConversation(myConversation);
      } else {
        message.conversationId = myConversation.id;
        conversationId = myConversation.id;
      }

      if (this.storage.activeConversationId !== conversationId) {
        myConversation.unreadCounter += 1;
      } else {
        readMessage(message.senderId);
      }

      this.storage.addMessage(message, conversationId, true);
      this.updateState();
    });

    this.socket.on("typing", ({ userId, isTyping, conversationId, content, sender }) => {
      if (this.eventHandlers.onUserTyping && sender !== this) {
        this.eventHandlers.onUserTyping(
          new UserTypingEvent({ userId, isTyping, conversationId, content, })
        );
      }
    });
  }

  async fetchParticipantsAndAddUser(userId) {
    const users = await axios.post(`${BACKEND_URL}/get-participants`);
    const photo = await axios.post(`${BACKEND_URL}/photos/by_user_id:main_photo`, { user_id: userId });

    users.data.forEach((user) => {
      if (!this.participants[user.user_id]) {
        this.participants[user.user_id] = user;
      }
    });

    const sender = new User({
      id: userId,
      presence: new Presence({ status: UserStatus.Available, description: "" }),
      username: this.participants[userId]?.username || userId,
      avatar: null,
      data: { isAdmin: this.participants[userId]?.privilege !== 0, isBlocked: this.participants[userId]?.blocked, isBeingBlocked: this.participants[userId]?.is_being_blocked },
    });
    
    if (photo?.data) {
      sender.avatar = {
        "thumbnail": `data:image/jpeg;base64,${photo.data["thumbnail"]}`,
        "photo_id": photo?.data?.["photo_id"]
      }
    }

    this.storage.addUser(sender);
  }

  waitForUser(callback, interval) {
    if (this.storage?.currentUser?.id) {
      callback();
    } else {
      setTimeout(() => { this.waitForUser(callback, interval); }, interval);
    }
  }

  sendMessage({ message, conversationId }) {
    const conversation = this.storage.getConversation(conversationId)[0];
    const receiver_user_id = conversation.participants?.[0].id;

    const payload = {
      message, conversationId, sender_user_id: message.senderId,
      receiver_user_id, content: message.content, date: Date.now(), 
    };

    this.socket.emit("message", payload);
    return message;
  }

  sendTyping({ isTyping, content, conversationId, userId }) {
    this.socket.emit("typing", { isTyping, content, conversationId, userId });
  }

  on(evtType, evtHandler) {
    const key = `on${evtType.charAt(0).toUpperCase()}${evtType.substring(1)}`;
    if (key in this.eventHandlers) this.eventHandlers[key] = evtHandler;
  }

  off(evtType) {
    const key = `on${evtType.charAt(0).toUpperCase()}${evtType.substring(1)}`;
    if (key in this.eventHandlers) this.eventHandlers[key] = () => { };
  }
}